import { isSameRange } from "@components/inputs/date/utils/DatePicker.utils";
import { capitalize } from "@utils/string";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { DASH_CHARACTER } from "../../../constants";
import { BasicInputSizes, IconSize } from "../../../enums";
import DateType, { getDateFormat, getUtcDayjs } from "../../../types/Date";
import memoizeOneWithParamsCheck from "../../../utils/memoizeOne";
import { isInterval, PredefinedFilter } from "../../conditionalFilterDialog/ConditionalFilterDialog.utils";
import { DateIcon } from "../../icon";
import { WithErrorAndTooltipProps } from "../formError/WithErrorAndTooltip";
import { getSharedInputProps, IInputProps, InputWithErrorAndTooltip } from "../input";
import { withFormatter } from "../input/WithFormatter";
import { IPickerProps } from "./DatePicker";
import DatePickerBase from "./DatePickerBase";
import { TDateRangePickerValue } from "./popup/Calendar.utils";
import { DateRangePickerPopup, IDateRangePickerPopupProps } from "./popup/DateRangePickerPopup";
import {
    formatDateInterval,
    formatDateVariants,
    IDayInterval,
    isValidDateInterval,
    longDateFormat,
    parseDateInterval
} from "./utils";

export const DateRangeInput = withFormatter<IInputProps & WithErrorAndTooltipProps, IDayInterval>({
    parser: (value) => parseDateInterval(value),
    formatter: formatDateInterval,
    isValid: isValidDateInterval
}, InputWithErrorAndTooltip);

interface IProps extends IPickerProps<TDateRangePickerValue>, WithTranslation {
    showRelativeValues?: boolean;
}

class DateRangePicker extends React.Component<IProps> {
    static defaultProps = {
        width: BasicInputSizes.L
    };

    get format(): string {
        const format = DateType.defaultDateFormat ?? this.props.format;

        return longDateFormat(getDateFormat(format));
    }

    parser = (value: string): IDayInterval => {
        const interval = parseDateInterval(value, formatDateVariants(this.format));

        return {
            from: interval.from,
            to: getUtcDayjs(interval.to).endOf("day").toDate()
        };
    };

    get pickerPopup(): React.ComponentType<IDateRangePickerPopupProps> {
        return DateRangePickerPopup;
    }

    get icon(): React.ReactElement {
        return <DateIcon width={IconSize.M}/>;
    }

    get placeholder(): string {
        // TODO should we localize placeholder? How?
        return [this.format, this.format].join(` ${DASH_CHARACTER} `).toLowerCase();
    }

    formatter = (value: TDateRangePickerValue): string => {
        if (!value) {
            return "";
        }

        if (isInterval(value)) {
            return formatDateInterval(value, this.format);
        }

        return capitalize(this.props.t(`Components:ValueHelper.${value}`));
    };

    isValid = (val: TDateRangePickerValue): boolean => {
        if (isInterval(val)) {
            return isValidDateInterval(val);
        }

        return Object.values(PredefinedFilter).includes(val);
    };

    getPopupProps = memoizeOneWithParamsCheck((): Partial<IDateRangePickerPopupProps> => {
        return {
            value: this.props.value,
            showRelativeValues: this.props.showRelativeValues,
            minDate: this.props.minDate,
            maxDate: this.props.maxDate
        };
    }, () => [this.props.value, this.props.showRelativeValues, this.props.minDate, this.props.maxDate]);

    getInputProps = memoizeOneWithParamsCheck(() => {
        return {
            formatter: this.formatter,
            parser: this.parser,
            isSame: isSameRange,
            isValid: this.isValid
        };
    }, () => [this.formatter, this.parser, this.isValid]);

    render = () => {
        return (
            <DatePickerBase {...getSharedInputProps(this.props)}
                            value={this.props.value}
                            popupProps={this.getPopupProps()}
                            width={this.props.width}
                            onChange={this.props.onChange}
                            onBlur={this.props.onBlur}
                            onFocus={this.props.onFocus}
                            onSelectionChange={this.props.onChange}
                            pickerPopup={this.pickerPopup}
                            inputComponent={DateRangeInput}
                            inputProps={this.getInputProps()}
                            icon={this.icon}
                            placeholder={this.placeholder}
            />
        );
    };
}

const DateRangePickerExtended = withTranslation(["Components"])(DateRangePicker);

export { DateRangePickerExtended as DateRangePicker, DateRangePicker as DateRangePickerClean };